import { createContext, useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { IKContext } from 'imagekitio-react';

import CryptoJS from 'crypto-js';

import UserApi from "../services/UserApi";
import CompanyApi from "../services/CompanyApi";
import SubscriptionApi from "../services/SubscriptionApi";

export const UserContext = createContext({ user: null, setUser: () => { } });

export function UserProvider({ children }) {
  const userApi = new UserApi();
  const [user, setUser] = useState(null);


  useEffect(() => {
    refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const refreshUser = async () => {
    const auth = getAuth();

    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      if (!authUser) {
        setUser(null);
        return;
      }
      let enriched = { ...authUser };
      try {
        const userResponse = await userApi.getUser(authUser.uid);
        if (userResponse.success) {
          enriched = { ...enriched, ...userResponse.user, user: userResponse.user || {} }; // Ensure user is always an object
        } else {
          console.warn("UserProvider: failed to fetch user data →", userResponse.error);
        }

        if (!enriched.companyId) {
          enriched.companyId = null; // Ensure companyId is set to null if not present
        }
        if (!enriched.subscriptionId) {
          enriched.subscriptionId = null; // Ensure subscriptionId is set to null if not present
        }
        const subscriptionApi = new SubscriptionApi();

        if (userResponse?.user?.companyId) {
          const companyApi = new CompanyApi();
          const companyResponse = await companyApi.getCompany(userResponse?.user?.companyId);
          if (companyResponse.success) {
            enriched.company = companyResponse.company || "Unknown Company"; // Default to "Unknown Company" if not set
          } else {
            console.warn("UserProvider: failed to fetch company data →", companyResponse.error);
          }

          const subscriptionResponse = await subscriptionApi.getSubscriptionsByCompanyId(enriched.companyId);
          if (subscriptionResponse.success && subscriptionResponse.subscriptions.length > 0) {
            enriched.subscriptionId = subscriptionResponse.subscriptions[0].id; // Assuming the first subscription is the active one
            enriched.subscription = subscriptionResponse.subscriptions[0]; // Default to basic if not set
          } else {
            console.warn("UserProvider: failed to fetch subscriptions →", subscriptionResponse.error);
          }
        } else {
          const subscriptionResponse = await subscriptionApi.getSubscriptionsByUserId(enriched.uid);
          if (subscriptionResponse.success && subscriptionResponse.subscriptions.length > 0) {
            enriched.subscriptionId = subscriptionResponse.subscriptions[0].id; // Assuming the first subscription is the active one
            enriched.subscription = subscriptionResponse.subscriptions[0]; // Default to basic if not set
          } else {
            console.warn("UserProvider: failed to fetch subscriptions →", subscriptionResponse.error);
          }
        }
      } catch (err) {
        console.warn("UserProvider: failed to fetch profile →", err);
      }
      setUser(enriched);
    });

    return unsubscribe;
  }

  const authenticator = async () => {
    const token = CryptoJS.lib.WordArray.random(16).toString(); // generate 16-byte hex string
    const expire = Math.floor(Date.now() / 1000) + 600; // 10 mins from now
    const signature = CryptoJS.HmacSHA1(token + expire, process.env.REACT_APP_IMAGEKIT_PRIVATE_KEY)
    return { token, expire, signature };
  };


  return (
    <UserContext.Provider value={{ user, setUser, refreshUser }}>
      <IKContext
        publicKey={process.env.REACT_APP_IMAGEKIT_PUBLIC_KEY}
        urlEndpoint={process.env.REACT_APP_IMAGEKIT_URL_ENDPOINT}
        authenticator={() => Promise.resolve(authenticator())}
      >
        {children}
      </IKContext>
    </UserContext.Provider>
  );
}
